import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Chat as ChatIcon,
  Comment as CommentIcon,
  Star as StarIcon,
  AccountBalance as AccountBalanceIcon,
  Savings as SavingsIcon,
  Report as ReportIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  AccountCircle,
  Logout,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { logout } from '../../store/slices/authSlice';
import NotificationDropdown from '../molecules/NotificationDropdown';
import NotificationTestButton from '../atoms/NotificationTestButton';

const drawerWidth = 240;

const FIREBASE_ENV = process.env.REACT_APP_FIREBASE_ENV || 'test';
const IS_PRODUCTION = FIREBASE_ENV === 'production';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const menuItems = [
  {
    text: 'ダッシュボード',
    icon: <DashboardIcon />,
    path: '/dashboard',
  },
  {
    text: 'ユーザー管理',
    icon: <PeopleIcon />,
    path: '/users',
  },
  {
    text: '商品管理',
    icon: <InventoryIcon />,
    path: '/products',
  },
  {
    text: '商品コメント',
    icon: <CommentIcon />,
    path: '/product-comments',
  },
  {
    text: '特集商品管理',
    icon: <StarIcon />,
    path: '/featured-products',
  },
  {
    text: 'ホーム設定',
    icon: <SettingsIcon />,
    path: '/home-settings',
  },
  {
    text: 'クーポン管理',
    icon: <StarIcon />,
    path: '/coupons',
  },
  {
    text: 'チュートリアル統計',
    icon: <StarIcon />,
    path: '/tutorial-stats',
  },
  {
    text: 'チャット管理',
    icon: <ChatIcon />,
    path: '/chats',
  },
  {
    text: '取引・決済管理',
    icon: <AccountBalanceIcon />,
    path: '/bank-transfers',
  },
  {
    text: '出金申請管理',
    icon: <SavingsIcon />,
    path: '/payout-requests',
  },
  {
    text: '通報・問い合わせ',
    icon: <ReportIcon />,
    path: '/reports',
  },
  {
    text: 'プッシュ通知',
    icon: <NotificationsActiveIcon />,
    path: '/notifications',
  },
  {
    text: 'お知らせ管理',
    icon: <NotificationsIcon />,
    path: '/announcements',
  },
  {
    text: '設定',
    icon: <SettingsIcon />,
    path: '/settings',
  },
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { unreadCount } = useSelector((state: RootState) => state.notifications);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
    handleProfileMenuClose();
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleProfileMenuClose();
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          アリクイ管理
        </Typography>
      </Toolbar>
      {!IS_PRODUCTION && (
        <Box sx={{ bgcolor: '#e3f2fd', color: '#1565c0', textAlign: 'center', py: 0.5, fontSize: '11px', fontWeight: 'bold' }}>
          テスト環境
        </Box>
      )}
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* 環境表示バナー（テスト環境は青帯、本番環境は非表示） */}
      {!IS_PRODUCTION && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.drawer + 2,
            bgcolor: '#1565c0',
            color: '#fff',
            textAlign: 'center',
            py: 0.3,
            fontSize: '12px',
            fontWeight: 'bold',
            letterSpacing: '2px',
          }}
        >
          ⚠ テスト環境（art-flea-test） — 本番データには影響しません
        </Box>
      )}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          ...(IS_PRODUCTION
            ? {}
            : { mt: '26px', bgcolor: '#1976d2' }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'アリクイ管理画面'}
          </Typography>

          {/* 開発用テストボタン */}
          {process.env.NODE_ENV === 'development' && (
            <NotificationTestButton />
          )}

          <Tooltip title="通知">
            <IconButton color="inherit" onClick={handleNotificationClick}>
              <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title="プロフィール">
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              onClick={handleProfileMenuOpen}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.name?.charAt(0) || <AccountCircle />}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleProfileClick}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              プロフィール
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              ログアウト
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ...(IS_PRODUCTION ? {} : { mt: '26px' }),
        }}
      >
        <Toolbar />
        {children}
      </Box>

      {/* 通知ドロップダウン */}
      <NotificationDropdown
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationClose}
      />
    </Box>
  );
};

export default AdminLayout; 